import axios from 'axios';
import { useState, useEffect, useRef, useCallback } from "react";
import { X, MapPin, RefreshCw, Users, AlertTriangle, Clock, Building2, ChevronRight, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { urlFichier } from '@/lib/fichiers';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const DEFAULT_CENTER = [46.8139, -71.2080]; // Québec City
const DEFAULT_ZOOM = 11;

// Cache géocodage pour éviter les appels répétés
const geocodeCache = {};

/** Normalise un chemin photo pour obtenir une URL absolue */
function buildPhotoUrl(photoPath) {
  if (!photoPath) return null;
  if (photoPath.startsWith('data:') || photoPath.startsWith('http')) return photoPath;
  if (photoPath.startsWith('/uploads/')) photoPath = `/api${photoPath}`;
  return urlFichier(`${API_URL}${photoPath}`);
}

// 🗺️ [v13.63 Phase 3 — 2026-02-28] Passage au backend central `/api/geo/geocode`
//   (Google Maps + fallback Nominatim + cache 24h partagé) au lieu d'appeler
//   Nominatim directement depuis le navigateur.
async function geocodeAddress(adresse, ville) {
  const query = `${adresse || ""} ${ville || "Québec"} QC Canada`.trim();
  if (geocodeCache[query]) return geocodeCache[query];
  try {
    const res = await axios.get(`${API_URL}/api/geo/geocode`, { params: { adresse: query } });
    if (res.status === 200 && res.data?.lat != null && res.data?.lng != null) {
      const coords = [res.data.lat, res.data.lng];
      geocodeCache[query] = coords;
      return coords;
    }
  } catch (e) {
    // ignore — le composant appelant affichera un placeholder
  }
  return null;
}

function formatDuration(minutes) {
  if (minutes < 60) return `${minutes}min`;
  const h = (minutes / 60).toFixed(2).replace('.', ',');
  return `${h}h`;
}

function LiveTimer({ punchInTime }) {
  const [elapsed, setElapsed] = useState("");
  useEffect(() => {
    const update = () => {
      const start = new Date(punchInTime);
      const diff = Math.floor((new Date() - start) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setElapsed(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
    };
    update();
    const iv = setInterval(update, 1000);
    return () => clearInterval(iv);
  }, [punchInTime]);
  return <span className="font-mono text-green-600 font-bold text-xs">{elapsed}</span>;
}

export default function LiveMapView({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarTab, setSidebarTab] = useState('employes'); // 'employes' | 'chantiers'
  const [statutFilter, setStatutFilter] = useState('all'); // 'all' | 'active' | 'terminated' | 'absent'
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshCountdown, setRefreshCountdown] = useState(60);
  const mapContainerRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markersRef = useRef([]);
  const autoRefreshRef = useRef(null);
  const countdownRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/punch/live-map`);
      if (res.status !== 200) throw new Error("Erreur chargement données");
      const json = res.data;
      setData(json);
      setRefreshCountdown(60);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Filtrage par statut : applique statutFilter aux listes utilisées partout
  const filtered = (() => {
    if (!data) return { actifs: [], termines: [], non: [], admin: [] };
    const f = statutFilter;
    return {
      actifs: f === 'all' || f === 'active' ? (data.punches_actifs || []) : [],
      termines: f === 'all' || f === 'terminated' ? (data.punches_termines || []) : [],
      non: f === 'all' || f === 'absent' ? (data.non_punches || []) : [],
      admin: f === 'all' || f === 'active' ? (data.punches_admin || []) : [],
    };
  })();

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(fetchData, 60000);
      countdownRef.current = setInterval(() => setRefreshCountdown(c => c > 0 ? c - 1 : 60), 1000);
    }
    return () => {
      clearInterval(autoRefreshRef.current);
      clearInterval(countdownRef.current);
    };
  }, [autoRefresh, fetchData]);

  // Fetch data on mount
  useEffect(() => { fetchData(); }, [fetchData]);

  // Init Leaflet map once mapContainerRef is available
  useEffect(() => {
    if (!mapContainerRef.current || leafletMapRef.current) return;
    const map = L.map(mapContainerRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    leafletMapRef.current = map;
    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Add/update markers when data changes
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!map || !data) return;

    // Clear old markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    const bounds = [];

    // ✅ Valide les coordonnées : rejette (0,0), NaN, ou hors zone Québec/Canada
    // Québec : lat ~44-63, lng ~-80 à -57 | on élargit un peu au cas où employé en voyage légitime
    const isValidCoord = (lat, lng) => {
      if (isNaN(lat) || isNaN(lng)) return false;
      if (lat === 0 && lng === 0) return false; // Gulf of Guinea = coord par défaut invalide
      // Rejet hors Amérique du Nord grossièrement
      if (lat < 25 || lat > 75) return false;
      if (lng < -140 || lng > -50) return false;
      return true;
    };

    const createMarker = (item, isPunched, status = null) => {
      const lat = parseFloat(item.latitude);
      const lng = parseFloat(item.longitude);
      if (!isValidCoord(lat, lng)) return;

      bounds.push([lat, lng]);

      const photoUrl = buildPhotoUrl(item.employee_photo);
      // ✅ 3 statuts : 'terminated' (vert pâle, journée finie), 'active' (vert vif), 'absent' (rouge)
      const effectiveStatus = status || (isPunched ? 'active' : 'absent');
      const borderColor =
        effectiveStatus === 'terminated' ? '#10B981' :
        effectiveStatus === 'active' ? '#16A34A' : '#DC2626';
      const bgColor =
        effectiveStatus === 'terminated' ? '#A7F3D0' :
        effectiveStatus === 'active' ? '#22C55E' : '#9CA3AF';
      const initials = (item.employee_nom || "?")[0].toUpperCase();

      const iconHtml = `
        <div style="display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3));">
          <div style="
            width:46px;height:46px;border-radius:50%;
            border:3px solid ${borderColor};
            overflow:hidden;background:${bgColor};
            display:flex;align-items:center;justify-content:center;
            position:relative;
          ">
            ${photoUrl
              ? `<img src="${photoUrl}" style="width:100%;height:100%;object-fit:cover;${effectiveStatus === 'terminated' ? 'opacity:0.85;' : ''}" onerror="this.style.display='none';this.nextSibling.style.display='flex';" />
                 <span style="display:none;position:absolute;inset:0;align-items:center;justify-content:center;color:white;font-weight:700;font-size:16px;">${initials}</span>`
              : `<span style="color:white;font-weight:700;font-size:16px;">${initials}</span>`
            }
            ${effectiveStatus === 'absent' ? `<div style="position:absolute;inset:0;background:rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;font-size:18px;">⚠️</div>` : ''}
            ${effectiveStatus === 'terminated' ? `<div style="position:absolute;bottom:-2px;right:-2px;width:18px;height:18px;background:white;border:2px solid #10B981;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;color:#10B981;">✓</div>` : ''}
          </div>
          <div style="
            margin-top:1px;background:white;border:1.5px solid ${borderColor};
            border-radius:3px;padding:1px 5px;font-size:10px;font-weight:700;
            color:#1e293b;white-space:nowrap;max-width:100px;overflow:hidden;text-overflow:ellipsis;
          ">${item.employee_nom.split(" ")[0]}</div>
          <div style="width:2px;height:6px;background:${borderColor};"></div>
        </div>
      `;

      const icon = L.divIcon({ html: iconHtml, className: "", iconSize: [100, 72], iconAnchor: [23, 70], popupAnchor: [0, -72] });

      const durationLabel =
        effectiveStatus === 'terminated' ? `Journée terminée — ${formatDuration(item.duree_minutes)}` :
        effectiveStatus === 'active' ? `Depuis ${formatDuration(item.duree_minutes)}` :
        "Non pointé aujourd'hui";

      const popup = `
        <div style="min-width:170px;font-family:system-ui;font-size:12px;">
          <p style="font-weight:700;margin:0 0 4px;">${item.employee_nom}</p>
          ${item.client_nom ? `<p style="color:#64748b;margin:0 0 2px;">📍 ${item.client_nom}</p>` : ""}
          ${item.adresse ? `<p style="color:#94a3b8;margin:0 0 4px;">${item.adresse}, ${item.ville}</p>` : ""}
          <span style="
            background:${effectiveStatus === 'terminated' ? '#d1fae5' : effectiveStatus === 'active' ? '#dcfce7' : '#fee2e2'};
            color:${effectiveStatus === 'terminated' ? '#065f46' : effectiveStatus === 'active' ? '#15803d' : '#dc2626'};
            padding:2px 8px;border-radius:9999px;font-weight:600;
          ">${effectiveStatus === 'terminated' ? '✓' : effectiveStatus === 'active' ? '✅' : '⚠️'} ${durationLabel}</span>
        </div>
      `;

      const marker = L.marker([lat, lng], { icon }).addTo(map);
      marker.bindPopup(popup);
      markersRef.current.push(marker);
    };

    // ✅ Filtrage strict : une coord valide = dans l'enveloppe Amérique du Nord
    const hasValidGeo = (i) => {
      if (!i.latitude || !i.longitude) return false;
      const la = parseFloat(i.latitude);
      const ln = parseFloat(i.longitude);
      return isValidCoord(la, ln);
    };

    // Punches actifs (with coords)
    (filtered.actifs).filter(hasValidGeo).forEach(item => createMarker(item, true, 'active'));

    // ✅ Punches TERMINÉS (vert pâle, journée terminée)
    (filtered.termines).filter(hasValidGeo).forEach(item => createMarker(item, true, 'terminated'));

    // Non-punched sync markers first (with coords)
    (filtered.non).filter(hasValidGeo).forEach(item => createMarker(item, false, 'absent'));

    // Admin punches (with coords)
    (filtered.admin).filter(hasValidGeo).forEach(item => createMarker(item, true, 'active'));

    // Geocode addresses for items without GPS and create markers
    const geocodeAndCreate = async () => {
      const BUREAU_CENTER = [46.8139, -71.2080]; // Québec — Bureau par défaut
      const itemsToGeocode = [
        ...filtered.actifs.filter(i => !hasValidGeo(i)).map(i => ({ ...i, _status: 'active' })),
        ...filtered.termines.filter(i => !hasValidGeo(i)).map(i => ({ ...i, _status: 'terminated' })),
        ...filtered.non.filter(i => !hasValidGeo(i)).map(i => ({ ...i, _status: 'absent' })),
      ];
      // Admin punches sans GPS valide → position "Bureau" par défaut
      const adminNoGps = (filtered.admin).filter(i => !hasValidGeo(i));
      for (let idx = 0; idx < adminNoGps.length; idx++) {
        const item = { ...adminNoGps[idx] };
        // Petit décalage aléatoire pour éviter le chevauchement
        item.latitude = BUREAU_CENTER[0] + (idx * 0.0005) - (adminNoGps.length * 0.00025);
        item.longitude = BUREAU_CENTER[1] + (idx * 0.0003);
        createMarker(item, true);
      }
      for (const item of itemsToGeocode) {
        if (item.adresse || item.ville) {
          const coords = await geocodeAddress(item.adresse, item.ville);
          if (coords) {
            item.latitude = coords[0];
            item.longitude = coords[1];
            createMarker(item, item._status !== 'absent', item._status);
          }
        }
      }
      // Refit bounds after geocoding (coords valides uniquement)
      const allBounds = [
        ...(data.punches_actifs || []).filter(hasValidGeo).map(i => [parseFloat(i.latitude), parseFloat(i.longitude)]),
        ...(data.non_punches || []).filter(hasValidGeo).map(i => [parseFloat(i.latitude), parseFloat(i.longitude)]),
      ].filter(c => isValidCoord(c[0], c[1]));
      if (allBounds.length > 0) {
        try { map.fitBounds(allBounds, { padding: [60, 60], maxZoom: 14 }); } catch (e) { console.error("Error:", e); }
      }
    };

    geocodeAndCreate();

    // Fit bounds si déjà disponibles (coords valides uniquement)
    const immediateBounds = [
      ...(data.punches_actifs || []).filter(hasValidGeo).map(i => [parseFloat(i.latitude), parseFloat(i.longitude)]),
      ...(data.non_punches || []).filter(hasValidGeo).map(i => [parseFloat(i.latitude), parseFloat(i.longitude)]),
    ];
    if (immediateBounds.length > 0) {
      try { map.fitBounds(immediateBounds, { padding: [60, 60], maxZoom: 14 }); } catch (e) { console.error("Error:", e); }
    }
  }, [data, statutFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPunched = (data?.total_punches || 0) + (data?.total_admin || 0);
  const totalTermines = data?.total_termines || 0;
  const totalNonPunched = data?.total_non_punches || 0;

  // Grouper par chantier (bon_travail_id + client_nom)
  const chantiers = (() => {
    if (!data) return [];
    const map = {};
    const addToChantier = (item, statut) => {
      const key = item.bon_travail_id || `no_bon_${item.client_nom}`;
      if (!map[key]) {
        map[key] = {
          key,
          client_nom: item.client_nom,
          adresse: item.adresse,
          ville: item.ville,
          chef_equipe_nom: item.chef_equipe_nom || '',
          employes: [],
          nb_punches: 0,
          nb_total: 0,
        };
      }
      map[key].employes.push({ ...item, statut });
      map[key].nb_total++;
      if (statut === 'punche') map[key].nb_punches++;
    };
    (filtered.actifs).forEach(p => addToChantier(p, 'punche'));
    (filtered.termines).forEach(p => addToChantier(p, 'termine'));
    (filtered.non).forEach(p => addToChantier(p, 'absent'));
    return Object.values(map).sort((a, b) => b.nb_punches - a.nb_punches);
  })();

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
      data-testid="live-map-view"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col"
        style={{ height: "85vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Employés — Vue en direct</h2>
              <p className="text-xs text-slate-500">Carte temps réel des chantiers actifs</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2">
              <span className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-full px-3 py-1 text-xs font-semibold text-green-700">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                {totalPunched} punchés
              </span>
              {totalTermines > 0 && (
                <span className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 text-xs font-semibold text-emerald-700" data-testid="badge-termines">
                  ✓ {totalTermines} terminés
                </span>
              )}
              <span className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-full px-3 py-1 text-xs font-semibold text-red-600">
                <AlertTriangle className="w-3 h-3" />
                {totalNonPunched} absents
              </span>
            </div>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`hidden sm:flex items-center gap-1 text-[10px] rounded-full px-2.5 py-1 border transition ${autoRefresh ? 'bg-green-50 border-green-300 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`}
              title={autoRefresh ? `Actualisation auto — ${refreshCountdown}s` : 'Actualisation manuelle'}
            >
              <Radio className={`w-3 h-3 ${autoRefresh ? 'animate-pulse' : ''}`} />
              {autoRefresh ? `${refreshCountdown}s` : 'Manuel'}
            </button>
            <Button variant="ghost" size="sm" onClick={fetchData} disabled={loading} title="Actualiser maintenant">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-live-map">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 min-h-0">
          {/* Map container */}
          <div className="flex-1 relative">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10 rounded-bl-2xl">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Chargement...</p>
                </div>
              </div>
            )}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                <div className="text-center text-red-500">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">{error}</p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={fetchData}>Réessayer</Button>
                </div>
              </div>
            )}
            <div ref={mapContainerRef} style={{ width: "100%", height: "100%", borderBottomLeftRadius: "1rem" }} />
          </div>

          {/* Employee/Chantier list sidebar */}
          <div className="w-64 shrink-0 border-l flex flex-col overflow-hidden">
            {/* ✅ Filtres rapides par statut (chips cliquables) */}
            <div className="flex gap-1 px-2 py-2 border-b bg-white shrink-0" data-testid="filter-statut-bar">
              {[
                { key: 'all',        label: 'Tous',     count: totalPunched + totalTermines + totalNonPunched, color: 'slate' },
                { key: 'active',     label: 'Actifs',   count: totalPunched, color: 'green' },
                { key: 'terminated', label: 'Terminés', count: totalTermines, color: 'emerald' },
                { key: 'absent',     label: 'Absents',  count: totalNonPunched, color: 'red' },
              ].map(chip => {
                const active = statutFilter === chip.key;
                const palette = {
                  slate:   { on: 'bg-slate-700 text-white border-slate-700', off: 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100' },
                  green:   { on: 'bg-green-600 text-white border-green-600', off: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' },
                  emerald: { on: 'bg-emerald-600 text-white border-emerald-600', off: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' },
                  red:     { on: 'bg-red-600 text-white border-red-600', off: 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100' },
                }[chip.color];
                return (
                  <button
                    key={chip.key}
                    onClick={() => setStatutFilter(chip.key)}
                    className={`flex-1 px-1.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wide border transition ${active ? palette.on : palette.off}`}
                    data-testid={`filter-chip-${chip.key}`}
                  >
                    {chip.label}
                    <span className={`ml-0.5 ${active ? 'opacity-90' : 'opacity-70'}`}>({chip.count})</span>
                  </button>
                );
              })}
            </div>

            {/* Tab switcher */}
            <div className="flex border-b bg-slate-50 shrink-0">
              <button
                onClick={() => setSidebarTab('employes')}
                className={`flex-1 py-2.5 text-[10px] font-bold uppercase tracking-wide flex items-center justify-center gap-1 transition ${sidebarTab === 'employes' ? 'text-blue-700 border-b-2 border-blue-500 bg-white' : 'text-slate-500 hover:bg-white'}`}
              >
                <Users className="w-3.5 h-3.5" /> Employés
              </button>
              <button
                onClick={() => setSidebarTab('chantiers')}
                className={`flex-1 py-2.5 text-[10px] font-bold uppercase tracking-wide flex items-center justify-center gap-1 transition ${sidebarTab === 'chantiers' ? 'text-blue-700 border-b-2 border-blue-500 bg-white' : 'text-slate-500 hover:bg-white'}`}
                data-testid="tab-vue-chantiers"
              >
                <Building2 className="w-3.5 h-3.5" /> Chantiers
              </button>
            </div>

            <div className="flex-1 overflow-y-auto text-sm">
              {sidebarTab === 'employes' ? (
                /* ── VUE EMPLOYÉS ── */
                <div className="divide-y">
                  {/* Punchés */}
                  {filtered.actifs.length > 0 && (
                    <>
                      <div className="px-3 py-1 bg-green-50">
                        <p className="text-[10px] font-bold text-green-700 uppercase">Sur chantier ({filtered.actifs.length})</p>
                      </div>
                      {filtered.actifs.map((item) => (
                        <div key={item.punch_id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50">
                          <div className="relative shrink-0">
                            {item.employee_photo ? (
                              <img src={buildPhotoUrl(item.employee_photo)} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-green-400" />
                            ) : (
                              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-green-400" style={{ background: item.employee_couleur }}>
                                {(item.employee_nom || "?")[0]}
                              </div>
                            )}
                            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-slate-800 truncate">{item.employee_nom}</p>
                            <p className="text-[10px] text-slate-400 truncate">{item.client_nom}</p>
                            {item.adresse && <p className="text-[10px] text-slate-400 truncate" data-testid={`punch-location-${item.punch_id}`}>{item.adresse}, {item.ville}</p>}
                            <div className="flex items-center gap-1 flex-wrap">
                              <Clock className="w-2.5 h-2.5 text-green-500" />
                              <LiveTimer punchInTime={item.punch_in_time} />
                              {item.gps_freshness === 'live' && (
                                <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1 py-0 rounded font-semibold" title="Position GPS en temps réel (< 10 min)">📍 Live</span>
                              )}
                              {item.gps_freshness === 'punch_in' && (
                                <span className="text-[9px] bg-amber-100 text-amber-700 px-1 py-0 rounded font-semibold" title="GPS au moment du pointage (peut avoir bougé)">📍 Punch-in</span>
                              )}
                              {item.gps_freshness === 'site' && (
                                <span className="text-[9px] bg-slate-100 text-slate-600 px-1 py-0 rounded font-semibold" title="Adresse du chantier (pas de GPS réel)">📍 Site</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {/* ✅ Journée terminée (vert pâle, n'apparaît plus en rouge) */}
                  {filtered.termines.length > 0 && (
                    <>
                      <div className="px-3 py-1 bg-emerald-50">
                        <p className="text-[10px] font-bold text-emerald-700 uppercase">✓ Journée terminée ({filtered.termines.length})</p>
                      </div>
                      {filtered.termines.map((item) => (
                        <div key={item.punch_id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50" data-testid={`termine-${item.punch_id}`}>
                          <div className="relative shrink-0">
                            {item.employee_photo ? (
                              <img src={buildPhotoUrl(item.employee_photo)} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-emerald-300" />
                            ) : (
                              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-emerald-300" style={{ background: item.employee_couleur }}>
                                {(item.employee_nom || "?")[0]}
                              </div>
                            )}
                            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-white rounded-full border-2 border-emerald-500 flex items-center justify-center text-[7px] font-bold text-emerald-700">✓</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-slate-700 truncate">{item.employee_nom}</p>
                            <p className="text-[10px] text-slate-400 truncate">{item.client_nom}</p>
                            <p className="text-[10px] text-emerald-600 font-medium">Journée terminée — {formatDuration(item.duree_minutes || 0)}</p>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {/* Non-punchés */}
                  {filtered.non.length > 0 && (
                    <>
                      <div className="px-3 py-1 bg-red-50">
                        <p className="text-[10px] font-bold text-red-600 uppercase">Non pointés ({filtered.non.length})</p>
                      </div>
                      {filtered.non.map((item, i) => (
                        <div key={`${item.employee_id}-${i}`} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50">
                          <div className="relative shrink-0">
                            {item.employee_photo ? (
                              <img src={buildPhotoUrl(item.employee_photo)} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-red-300 opacity-70" />
                            ) : (
                              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-red-300 opacity-70" style={{ background: item.employee_couleur }}>
                                {(item.employee_nom || "?")[0]}
                              </div>
                            )}
                            <span className="absolute -bottom-0.5 -right-0.5 text-xs">!</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-slate-600 truncate">{item.employee_nom}</p>
                            <p className="text-[10px] text-slate-400 truncate">{item.client_nom}</p>
                            <p className="text-[10px] text-red-400 font-medium">Non pointé</p>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {/* Admin punchés */}
                  {filtered.admin.length > 0 && (
                    <>
                      <div className="px-3 py-1 bg-blue-50">
                        <p className="text-[10px] font-bold text-blue-700 uppercase" data-testid="admin-punches-header">Bureau ({filtered.admin.length})</p>
                      </div>
                      {filtered.admin.map((item) => (
                        <div key={item.punch_id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50">
                          <div className="relative shrink-0">
                            {item.employee_photo ? (
                              <img src={buildPhotoUrl(item.employee_photo)} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-blue-400" />
                            ) : (
                              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-blue-400 bg-blue-500">
                                {(item.employee_nom || "?")[0]}
                              </div>
                            )}
                            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-slate-800 truncate">{item.employee_nom}</p>
                            <p className="text-[10px] text-blue-500">Admin</p>
                            <div className="flex items-center gap-1">
                              <Clock className="w-2.5 h-2.5 text-blue-500" />
                              <LiveTimer punchInTime={item.punch_in_time} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {!loading && !error && totalPunched === 0 && totalNonPunched === 0 && totalTermines === 0 && (
                    <div className="flex flex-col items-center justify-center h-40 text-slate-300">
                      <Users className="w-8 h-8 mb-2" />
                      <p className="text-xs text-center px-4">Aucun employé assigné aujourd'hui</p>
                    </div>
                  )}
                </div>
              ) : (
                /* ── VUE CHANTIERS ── */
                <div className="divide-y">
                  {chantiers.length === 0 && !loading && (
                    <div className="flex flex-col items-center justify-center h-40 text-slate-300">
                      <Building2 className="w-8 h-8 mb-2" />
                      <p className="text-xs text-center px-4">Aucun chantier actif aujourd'hui</p>
                    </div>
                  )}
                  {chantiers.map((chantier) => (
                    <div key={chantier.key} className="px-3 py-2.5">
                      {/* Chantier header */}
                      <div className="flex items-start gap-2 mb-2">
                        <div className={`shrink-0 w-7 h-7 rounded-lg flex items-center justify-center ${chantier.nb_punches > 0 ? 'bg-green-100' : 'bg-red-50'}`}>
                          <Building2 className={`w-3.5 h-3.5 ${chantier.nb_punches > 0 ? 'text-green-600' : 'text-red-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <p className="text-xs font-bold text-slate-800 truncate flex-1">{chantier.client_nom}</p>
                            {/* 🆕 2026-04-30 — Badge synthétique X/Y présents (punchés + terminés / total cédulés) */}
                            {(() => {
                              const nbPresents = (chantier.employes || []).filter(e => e.statut === 'punche' || e.statut === 'termine').length;
                              const nbTotal = chantier.nb_total || 0;
                              if (nbTotal === 0) return null;
                              const ratio = nbPresents / nbTotal;
                              const cls = nbPresents === nbTotal
                                ? 'bg-green-100 text-green-800 border-green-300'
                                : ratio >= 0.5
                                  ? 'bg-amber-100 text-amber-800 border-amber-300'
                                  : 'bg-red-100 text-red-800 border-red-300';
                              return (
                                <span
                                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${cls}`}
                                  data-testid={`chantier-presence-${chantier.key}`}
                                  title={`${nbPresents} sur ${nbTotal} cédulé${nbTotal > 1 ? 's' : ''} aujourd'hui se sont présenté${nbPresents > 1 ? 's' : ''}`}
                                >
                                  {nbPresents}/{nbTotal} présents
                                </span>
                              );
                            })()}
                          </div>
                          {chantier.adresse && <p className="text-[10px] text-slate-400 truncate">{chantier.adresse}, {chantier.ville}</p>}
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span className="text-[10px] font-semibold text-green-600">{chantier.nb_punches} punchés</span>
                            {(() => {
                              const nbTermines = (chantier.employes || []).filter(e => e.statut === 'termine').length;
                              return nbTermines > 0 && <span className="text-[10px] font-semibold text-emerald-600">✓ {nbTermines} terminés</span>;
                            })()}
                            {(() => {
                              const nbAbsents = (chantier.employes || []).filter(e => e.statut === 'absent').length;
                              return nbAbsents > 0 && <span className="text-[10px] font-semibold text-red-500">{nbAbsents} absents</span>;
                            })()}
                          </div>
                        </div>
                      </div>
                      {/* Équipe */}
                      <div className="flex flex-col gap-1 pl-9">
                        {chantier.employes.map((emp, idx) => (
                          <div key={emp.id || emp._id || idx} className="flex items-center gap-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                              emp.statut === 'punche' ? 'bg-green-500'
                              : emp.statut === 'termine' ? 'bg-emerald-400'
                              : 'bg-red-400'
                            }`} />
                            <span className="text-[11px] text-slate-700 truncate">{emp.employee_nom}</span>
                            {emp.statut === 'punche' && emp.punch_in_time && (
                              <LiveTimer punchInTime={emp.punch_in_time} />
                            )}
                            {emp.statut === 'termine' && (
                              <span className="text-[10px] text-emerald-600 font-medium">✓ terminé</span>
                            )}
                            {emp.statut === 'absent' && (
                              <span className="text-[10px] text-red-400 font-medium">absent</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Légende + auto-refresh info */}
            <div className="px-3 py-2 border-t bg-slate-50 shrink-0 space-y-1">
              <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                <span className="w-3 h-3 rounded-full bg-green-500 border border-green-600 inline-block" />
                Pointé — chrono en direct
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                <span className="w-3 h-3 rounded-full bg-emerald-300 border border-emerald-500 inline-block" />
                Journée terminée
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                <span className="w-3 h-3 rounded-full bg-red-400 border border-red-500 inline-block" />
                Non pointé
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                <span className="w-3 h-3 rounded-full bg-blue-500 border border-blue-600 inline-block" />
                Administratif
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .leaflet-container { border-bottom-left-radius: 1rem; }
      `}</style>
    </div>
  );
}
